import AttachmentUrlType from 'owa-files-url/lib/schema/AttachmentUrlType';
import getImageElementsByContentId from './getImageElementsByContentId';
import InlineImageViewState from '../schema/InlineImageViewState';
import onImageLoad from './onImageLoad';
import onImageError from './onImageError';
import { EditorViewState, lazyOperateContent } from 'owa-editor';
import { attachmentStore, isSmimeAttachment } from 'owa-attachment-model-store';
import { getAttachmentUrlForClassic } from 'owa-attachment-url';
import { ClientAttachmentId } from 'owa-client-ids';
import { SmimeAttachmentType } from 'owa-attachment-model-store';
import { lazyLogImageLoadError, lazyLoadFromAttachmentServiceUrl } from 'owa-inline-image-loader';
import { CONTEXT_UPLOAD } from './constants';

// The context used for S/MIME inline
const SMIME_INLINE = 'SmimeInline';

// File type
const FILETYPE_ATTACHMENT = 'Attachment';

// Error
const ERROR_NULL_ATTACHMENT_URL = 'NullAttachmentUrl';

export default function processInlineImageAfterUploadSuccess(
    viewState: InlineImageViewState,
    editorViewState: EditorViewState,
    attachmentId: ClientAttachmentId,
    temporaryId: string
) {
    const attachment = attachmentStore().attachments.get(attachmentId.Id).model;
    const isSmime = isSmimeAttachment(attachmentId.Id);
    const downloadUrl = isSmime
        ? getAttachmentUrlForSmime(attachment)
        : getAttachmentUrlForClassic(attachmentId, AttachmentUrlType.Preview);
    if (downloadUrl) {
        return replacePlaceholderImage(
            viewState,
            editorViewState,
            temporaryId,
            downloadUrl,
            attachment.ContentId,
            attachment.Size,
            attachment.ContentType,
            isSmime
        );
    } else {
        lazyLogImageLoadError
            .import()
            .then(imageLoadErrorLogger =>
                imageLoadErrorLogger(
                    FILETYPE_ATTACHMENT /* type */,
                    '' /* howLoad */,
                    ERROR_NULL_ATTACHMENT_URL /* error */,
                    '' /* statusCode */,
                    isSmime ? SMIME_INLINE : CONTEXT_UPLOAD /* context */
                )
            );
        return Promise.resolve();
    }
}

function getAttachmentUrlForSmime(attachment: SmimeAttachmentType): string {
    const { Content, ContentType } = attachment;
    return 'data:' + ContentType + ';base64,' + Content;
}

async function replacePlaceholderImage(
    viewState: InlineImageViewState,
    editorViewState: EditorViewState,
    temporaryId: string,
    imageUrl: string,
    contentId: string,
    size: number,
    contentType: string,
    isSmime: boolean = false
) {
    const operateContent = await lazyOperateContent.import();
    const loadFromAttachmentServiceUrl = await lazyLoadFromAttachmentServiceUrl.import();
    let targetElement: HTMLImageElement;
    let isImageElementAlreadyPresent = false;

    operateContent(editorViewState, editor => {
        if (isSmime) {
            const elements = getImageElementsByContentId(editor, contentId);
            if (elements.length > 0) {
                targetElement = elements[0];
                isImageElementAlreadyPresent = true;
            }
        }
        if (!targetElement) {
            targetElement = editor.getDocument().createElement('img');
            targetElement.setAttribute('originalSrc', 'cid:' + contentId);
            targetElement.setAttribute('size', size.toString());
            targetElement.setAttribute('contentType', contentType);
            targetElement.style.maxWidth = '100%';
        }

        return null;
    });

    loadFromAttachmentServiceUrl(
        targetElement,
        imageUrl,
        isSmime ? SMIME_INLINE : CONTEXT_UPLOAD,
        isImageElementAlreadyPresent
            ? null
            : imageElement => {
                  onImageLoad(viewState, editorViewState, temporaryId, imageElement);
              },
        isImageElementAlreadyPresent
            ? null
            : imageElement => {
                  onImageError(viewState, editorViewState, temporaryId);
              }
    );
}
